import { Link } from '@tanstack/react-router'
import { useTranslation } from 'react-i18next'

export function HomePage() {
  const { t } = useTranslation()

  return (
    <>
      <Hero t={t} />
      <TrustStrip t={t} />
      <Services t={t} />
      <Features t={t} />
      <Stats t={t} />
      <Download t={t} />
    </>
  )
}

type T = (key: string) => string

function Hero({ t }: { t: T }) {
  return (
    <section className="relative overflow-hidden bg-gradient-to-br from-[#0a2540] via-[#0d3b66] to-[#0a2540] text-white">
      <div className="absolute inset-0 opacity-20 [background-image:radial-gradient(circle_at_20%_30%,rgba(250,204,21,0.4),transparent_50%),radial-gradient(circle_at_80%_70%,rgba(56,189,248,0.3),transparent_50%)]" />
      <div className="relative mx-auto grid max-w-6xl items-center gap-12 px-4 py-20 md:grid-cols-2 md:py-28">
        <div>
          <h1 className="text-4xl font-bold leading-tight tracking-tight sm:text-5xl md:text-6xl">
            {t('home.headline')}
          </h1>
          <p className="mt-6 max-w-xl text-lg text-slate-200">{t('home.sub')}</p>
          <div className="mt-8 flex flex-wrap gap-3">
            <Link
              to="/map"
              className="rounded-lg bg-amber-400 px-6 py-3 font-semibold text-slate-900 shadow-lg shadow-amber-400/20 transition hover:bg-amber-300"
            >
              {t('home.cta_map')}
            </Link>
            <Link
              to="/login"
              className="rounded-lg border border-white/30 bg-white/5 px-6 py-3 font-semibold text-white backdrop-blur transition hover:bg-white/10"
            >
              {t('home.cta_login')}
            </Link>
          </div>
          <div className="mt-8 flex flex-wrap gap-3">
            <StoreBadge label={t('home.badge_app_store')} sub="App Store" icon={<AppleIcon />} />
            <StoreBadge label={t('home.badge_play')} sub="Google Play" icon={<PlayIcon />} />
          </div>
        </div>
        <PhoneMockup />
      </div>
    </section>
  )
}

function TrustStrip({ t }: { t: T }) {
  return (
    <div className="border-y border-slate-200 bg-white">
      <div className="mx-auto max-w-6xl px-4 py-4 text-center text-sm font-medium uppercase tracking-wider text-slate-500">
        {t('home.trust_strip')}
      </div>
    </div>
  )
}

function Services({ t }: { t: T }) {
  const items = [
    { icon: <PinIcon />, title: t('home.service_find_title'), desc: t('home.service_find_desc') },
    { icon: <CardIcon />, title: t('home.service_pay_title'), desc: t('home.service_pay_desc') },
    {
      icon: <TicketIcon />,
      title: t('home.service_permits_title'),
      desc: t('home.service_permits_desc'),
    },
    {
      icon: <ShieldIcon />,
      title: t('home.service_fines_title'),
      desc: t('home.service_fines_desc'),
    },
  ]
  return (
    <section id="services" className="bg-slate-50 py-20">
      <div className="mx-auto max-w-6xl px-4">
        <div className="mx-auto max-w-2xl text-center">
          <h2 className="text-3xl font-bold tracking-tight sm:text-4xl">
            {t('home.services_title')}
          </h2>
          <p className="mt-4 text-lg text-slate-600">{t('home.services_sub')}</p>
        </div>
        <div className="mt-12 grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
          {items.map((it) => (
            <div
              key={it.title}
              className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm transition hover:shadow-md"
            >
              <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-amber-100 text-[#0d3b66]">
                {it.icon}
              </div>
              <h3 className="mt-5 text-lg font-semibold text-slate-900">{it.title}</h3>
              <p className="mt-2 text-sm text-slate-600">{it.desc}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  )
}

function Features({ t }: { t: T }) {
  const items = [
    { title: t('home.feature_sms_title'), desc: t('home.feature_sms_desc') },
    { title: t('home.feature_anpr_title'), desc: t('home.feature_anpr_desc') },
    { title: t('home.feature_payme_title'), desc: t('home.feature_payme_desc') },
    { title: t('home.feature_history_title'), desc: t('home.feature_history_desc') },
    { title: t('home.feature_family_title'), desc: t('home.feature_family_desc') },
    { title: t('home.feature_lang_title'), desc: t('home.feature_lang_desc') },
  ]
  return (
    <section id="features" className="bg-white py-20">
      <div className="mx-auto max-w-6xl px-4">
        <h2 className="text-center text-3xl font-bold tracking-tight sm:text-4xl">
          {t('home.features_title')}
        </h2>
        <div className="mt-12 grid gap-8 sm:grid-cols-2 lg:grid-cols-3">
          {items.map((it) => (
            <div key={it.title} className="flex gap-4">
              <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-[#0d3b66] text-amber-300">
                <CheckIcon />
              </div>
              <div>
                <h3 className="text-base font-semibold text-slate-900">{it.title}</h3>
                <p className="mt-1 text-sm text-slate-600">{it.desc}</p>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  )
}

function Stats({ t }: { t: T }) {
  const items = [
    { value: '120+', label: t('home.stats_zones') },
    { value: '3', label: t('home.stats_cities') },
    { value: '3', label: t('home.stats_languages') },
    { value: '99.9%', label: t('home.stats_uptime') },
  ]
  return (
    <section className="bg-[#0a2540] py-16 text-white">
      <div className="mx-auto grid max-w-6xl gap-8 px-4 text-center sm:grid-cols-2 lg:grid-cols-4">
        {items.map((it) => (
          <div key={it.label}>
            <div className="text-4xl font-bold text-amber-400 sm:text-5xl">{it.value}</div>
            <div className="mt-2 text-sm uppercase tracking-wider text-slate-300">{it.label}</div>
          </div>
        ))}
      </div>
    </section>
  )
}

function Download({ t }: { t: T }) {
  return (
    <section id="download" className="bg-slate-50 py-20">
      <div className="mx-auto max-w-4xl px-4 text-center">
        <h2 className="text-3xl font-bold tracking-tight sm:text-4xl">
          {t('home.download_title')}
        </h2>
        <p className="mx-auto mt-4 max-w-2xl text-lg text-slate-600">{t('home.download_sub')}</p>
        <div className="mt-8 flex flex-wrap justify-center gap-4">
          <StoreBadge
            label={t('home.badge_app_store')}
            sub="App Store"
            icon={<AppleIcon />}
            dark
          />
          <StoreBadge label={t('home.badge_play')} sub="Google Play" icon={<PlayIcon />} dark />
        </div>
      </div>
    </section>
  )
}

function StoreBadge({
  label,
  sub,
  icon,
  dark,
}: {
  label: string
  sub: string
  icon: React.ReactNode
  dark?: boolean
}) {
  const base = dark
    ? 'bg-slate-900 text-white hover:bg-slate-800'
    : 'bg-white/10 text-white border border-white/30 hover:bg-white/20 backdrop-blur'
  return (
    <a
      href="#"
      className={`inline-flex items-center gap-3 rounded-xl px-5 py-3 transition ${base}`}
    >
      <span className="text-2xl">{icon}</span>
      <span className="text-left leading-tight">
        <span className="block text-[10px] uppercase tracking-wider opacity-80">{label}</span>
        <span className="block text-base font-semibold">{sub}</span>
      </span>
    </a>
  )
}

function PhoneMockup() {
  return (
    <div className="relative mx-auto w-full max-w-sm">
      <div className="relative aspect-[9/19] rounded-[2.5rem] border-[10px] border-slate-900 bg-slate-900 shadow-2xl">
        <div className="absolute left-1/2 top-2 h-5 w-24 -translate-x-1/2 rounded-full bg-slate-900" />
        <div className="absolute inset-2 overflow-hidden rounded-[1.8rem] bg-gradient-to-br from-sky-100 to-emerald-100">
          <div className="absolute inset-0 [background-image:linear-gradient(rgba(13,59,102,0.08)_1px,transparent_1px),linear-gradient(90deg,rgba(13,59,102,0.08)_1px,transparent_1px)] [background-size:24px_24px]" />
          <div className="absolute left-6 top-10 h-8 w-8 rounded-full bg-amber-400 ring-4 ring-amber-200" />
          <div className="absolute right-10 top-24 h-6 w-6 rounded-full bg-emerald-500 ring-4 ring-emerald-200" />
          <div className="absolute bottom-32 left-12 h-6 w-6 rounded-full bg-emerald-500 ring-4 ring-emerald-200" />
          <div className="absolute right-6 bottom-20 h-8 w-8 rounded-full bg-sky-500 ring-4 ring-sky-200" />
          <div className="absolute inset-x-3 bottom-3 rounded-2xl bg-white p-3 shadow-lg">
            <div className="text-[10px] uppercase tracking-wider text-slate-500">Zone A · 01A123BC</div>
            <div className="mt-1 flex items-baseline justify-between">
              <div className="text-lg font-bold text-slate-900">01:24:00</div>
              <div className="text-sm font-semibold text-emerald-600">3 600 so'm</div>
            </div>
            <div className="mt-2 h-1.5 overflow-hidden rounded-full bg-slate-100">
              <div className="h-full w-2/3 rounded-full bg-amber-400" />
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

function PinIcon() {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="h-6 w-6">
      <path d="M12 21s-7-7.58-7-12a7 7 0 1 1 14 0c0 4.42-7 12-7 12Z" />
      <circle cx="12" cy="9" r="2.5" />
    </svg>
  )
}
function CardIcon() {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="h-6 w-6">
      <rect x="3" y="6" width="18" height="13" rx="2" />
      <path d="M3 10h18" />
    </svg>
  )
}
function TicketIcon() {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="h-6 w-6">
      <path d="M3 8a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2v2a2 2 0 0 0 0 4v2a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-2a2 2 0 0 0 0-4Z" />
      <path d="M9 6v12" strokeDasharray="2 2" />
    </svg>
  )
}
function ShieldIcon() {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="h-6 w-6">
      <path d="M12 3 4 6v6c0 5 3.5 8.5 8 9 4.5-.5 8-4 8-9V6Z" />
      <path d="m9 12 2 2 4-4" />
    </svg>
  )
}
function CheckIcon() {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" className="h-5 w-5">
      <path d="m5 12 5 5L20 7" />
    </svg>
  )
}
function AppleIcon() {
  return (
    <svg viewBox="0 0 24 24" fill="currentColor" className="h-7 w-7">
      <path d="M16.4 12.6c0-2.3 1.9-3.4 2-3.5-1.1-1.6-2.8-1.8-3.4-1.8-1.5-.2-2.8.9-3.6.9-.7 0-1.9-.9-3.1-.8-1.6 0-3 .9-3.9 2.4-1.7 2.9-.4 7.1 1.2 9.5.8 1.1 1.7 2.4 3 2.4 1.2-.1 1.6-.8 3-.8 1.4 0 1.8.8 3 .8 1.3 0 2.1-1.2 2.9-2.3.9-1.3 1.3-2.6 1.3-2.7 0 0-2.5-1-2.5-3.9zM14.2 5.7c.6-.8 1.1-1.9 1-3-1 0-2.1.6-2.8 1.4-.6.7-1.2 1.8-1 2.9 1.1.1 2.2-.6 2.8-1.3z" />
    </svg>
  )
}
function PlayIcon() {
  return (
    <svg viewBox="0 0 24 24" fill="currentColor" className="h-7 w-7">
      <path d="M3.6 2.3c-.3.3-.5.8-.5 1.4v16.6c0 .6.2 1.1.5 1.4l9.4-9.7L3.6 2.3zM14.6 13.5l2.5 2.6-12.4 7c-.4.2-.7.2-1 0l10.9-9.6zM18 9.4l-3 1.7-2.6 2.4 2.6 2.5 3.1 1.8c.9.5 1.6 0 1.6-1V10.4c0-1-.7-1.5-1.7-1zM4.7 1.9l12.4 7-2.6 2.5L4.1 1.9c.3-.2.6-.2 1 0z" />
    </svg>
  )
}
